import React, { useMemo } from "react";
import { ChannelAccount, Entity, ProvisionalBody, ProvisionalPost } from "../../types";
import { PubError } from "../usePubValidation";
import { displayableUrl } from "../utils/various";
import { PhoneIcon } from "@heroicons/react/20/solid";

// --- Composant principal ---

type Props = {
  header: JSX.Element;
  body: ProvisionalBody;
  errors: PubError[];
  variables: Record<string, string | null>;
  mentions?: { [k: string]: Entity };
  channelAccount?: ChannelAccount;
  poc?: ProvisionalPost["post_on_channels_attributes"][0];
};

const PostPreview = ({
  errors,
  body,
  variables,
  header,
  mentions,
  channelAccount,
  poc,
}: Props) => {
  const maxChar = errors.find((e) => e.errorIdentifier == "character_limit")
    ?.shouldBe as number;
  // Un lien affiché prend la place du visuel (pas pour CALL : le tel: n'est pas un lien
  // affiché), comme Body#displayed_link? côté serveur.
  const showsLink = Boolean(body.link_url && body.link_cta !== "call_now");

  const generateHtml = (input: string) => {
    return <div dangerouslySetInnerHTML={{ __html: input }} />;
  };
  const generateSpan = (key: string, value: Entity) => {
    const isHighlighted = mentions?.[key].uri;
    return `<span class="${isHighlighted
      ? "font-bold px-1 py-1 bg-brand_focus bg-opacity-20 text-brand_darker rounded-md"
      : ""
      }">${isHighlighted ? "@" : ""}${value.name}</span>`;
  };
  const parseMessage = (
    message: string,
    replacements: Record<string, string | null>,
  ) => {
    // on trie pour éviter les erreurs de remplacement pour les clés proches comme $CONNECT et $CONNECT_CODE
    return Object.entries(replacements)
      .sort(([a], [b]) => b.length - a.length)
      .reduce((accMessage, [key, value]) => {
        return accMessage.replaceAll(key, value ?? "");
      }, message ?? "");
  };

  const parsedMessage = useMemo(
    () => parseMessage(body.message, variables),
    [body.message, variables],
  );

  const parsedMentions = useMemo(
    () =>
      parseMessage(
        parsedMessage,
        Object.fromEntries(
          Object.entries(mentions ?? {}).map(([key, value]) => [
            key,
            generateSpan(key, value),
          ]),
        ),
      ),
    [parsedMessage, mentions],
  );

  const validMessage = (mentions ? parsedMentions : parsedMessage).slice(
    0,
    maxChar,
  );
  const extraMessage = maxChar ? parsedMessage.slice(maxChar) : "";

  const parsedLink = useMemo(
    () =>
      Object.entries(variables).reduce(
        (accMessage, [key, value]) => accMessage.replaceAll(key, value ?? ""),
        body.link_url ?? "",
      ),
    [body.link_url],
  );

  const finalUrl = parsedLink.startsWith("tel:")
    ? parsedLink
    : "https://" + parsedLink?.replaceAll("https://", "");

  // erreur prioritaire si post non diffusé : on n'affiche qu'elle, mais l'aperçu du
  // contenu reste visible (le cadre rouge et le message suffisent à signaler le blocage)
  const totalError = errors.find((e) => e.shouldBe === true);
  const errorsToDisplay = totalError ? [totalError] : errors;

  return (
    <div
      className={`flex shadow flex-col border rounded w-full max-w-lg snap-center h-fit ${errors.length > 0 ? "border-red-500 ring-4 ring-red-200" : ""
        }`}
    >
      {header}
      <div className="text-center">
        {errorsToDisplay.map(({ message, inactionStrategyMessage }) => (
          <p key={message} className="px-4 text-xs text-red-500">
            {inactionStrategyMessage}
          </p>
        ))}
      </div>
      <div className="p-4 text-sm text-gray-700 overflow-y-auto max-h-80 whitespace-pre-wrap">
        {generateHtml(validMessage)}
        <span className="text-gray-400">{extraMessage}</span>
      </div>
      {body.link_cta === "call_now" && (
        <div className="px-4 pt-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-blue-200 bg-blue-50 text-sm font-medium text-blue-700">
            <PhoneIcon className="size-3.5" />
            Appeler
          </span>
        </div>
      )}
      <div className="flex justify-start">
        {/* La zone média vient de la composition (même vue readonly que posts#show), servie
            serveur dans un frame synchronisé par le broadcast-signal — le mockup suit le
            brouillon édité dans l'îlot, jamais un état client figé. */}
        {!showsLink && body.composition_id && React.createElement("turbo-frame", {
          // Une autre composition = un autre frame : muter id et src en place laisse Turbo
          // charger le nouveau src avec l'ancien id en tête, et la réponse « ne contient pas
          // le frame attendu ».
          key: body.composition_id,
          id: `media_preview_composition_${body.composition_id}_poc_${poc?.channel_account_id ?? "solo"}`,
          src: `/compositions/${body.composition_id}?variant=media_preview`,
          class: "block w-full",
          refresh: "morph",
          // Sur l'élément vivant : Turbo ne copie pas les attributs du frame de réponse,
          // et c'est ce sélecteur que vise le broadcast-signal (reload_frame).
          "data-composition-signal": body.composition_id,
        })}
        {showsLink && (
          <a
            className="block p-2 w-full"
            href={finalUrl}
            target="_blank"
            rel="noreferrer"
          >
            <div className="w-full flex flex-row items-center rounded-md bg-white overflow-hidden space-x-2 border border-slate-300 hover:bg-slate-100">
              <div className="h-full w-full">
                <div className="space-y-0.5 h-16 p-1">
                  <p className="font-medium text-gray-700">
                    {finalUrl && displayableUrl(finalUrl)}
                  </p>
                  <p className="text-sm text-gray-500 line-clamp-2">
                    {body.link_description}
                  </p>
                </div>
              </div>
            </div>
          </a>
        )}
      </div>
    </div>
  );
};

export default PostPreview;
